import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Newspaper, Search, Save, Plus, Trash2, FolderOpen, Loader2, ImageDown, Link2, Eye, Copy, Store, Globe, Upload } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_AVIS_PUBLIC_BASE || (typeof window !== "undefined" ? window.location.origin : "");
const avisImg = (p) => `${API_BASE}/avis/image/${p}`;
const STORES = [{ v: "falster", l: "Falster" }, { v: "ringsted", l: "Ringsted" }];
const kr = (n) => (Number(n || 0)).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isoWeek = (s) => {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
};

export function TilbudsAvis() {
  const [store, setStore] = useState("falster");
  const [name, setName] = useState("");
  const [published, setPublished] = useState(false);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [items, setItems] = useState([]);
  const [slug, setSlug] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(null);
  const searchRef = useRef(null);

  const loadList = async () => { try { const { data } = await api.get("/avis/list"); setList(data); } catch { /* */ } };
  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      try { const { data } = await api.get(`/avis/catalog/search`, { params: { store, q } }); setResults(data); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => searchRef.current && clearTimeout(searchRef.current);
  }, [search, store]);

  const addItem = (p) => {
    if (items.some((it) => it.productno === p.productno)) { toast.info("Varen er allerede tilføjet"); return; }
    setItems((arr) => [...arr, { productno: p.productno, title: p.title, ean: p.ean || "", normal_price: p.price || 0, offer_price: 0, cost: p.cost || 0, image_path: "" }]);
    setSearch(""); setResults([]);
  };
  const upd = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const calc = (offer, cost) => {
    const o = num(offer), c = Number(cost) || 0;
    if (!o) return null;
    const exVat = o / 1.25;
    const profit = exVat - c;
    const dg = exVat > 0 ? (profit / exVat) * 100 : 0;
    return { profit, dg };
  };

  const fetchImage = async (i) => {
    const it = items[i];
    if (!it.ean) { toast.error("Varen har ingen stregkode — indsæt en billed-URL i stedet"); return; }
    setImgBusy(i);
    try { const { data } = await api.post(`/avis/fetch-image`, { ean: it.ean }); upd(i, { image_path: data.image_path }); toast.success("Billede hentet"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente billede"); }
    finally { setImgBusy(null); }
  };
  const imageFromUrl = async (i) => {
    const url = window.prompt("Indsæt billed-URL (fx fra en produktside):");
    if (!url) return;
    setImgBusy(i);
    try { const { data } = await api.post(`/avis/fetch-image`, { url }); upd(i, { image_path: data.image_path }); toast.success("Billede hentet"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente billede"); }
    finally { setImgBusy(null); }
  };
  const uploadImage = async (i, file) => {
    if (!file) return;
    setImgBusy(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/avis/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(i, { image_path: data.image_path });
      toast.success("Billede uploadet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fejlede"); }
    finally { setImgBusy(null); }
  };

  const save = async (publishNow) => {
    if (items.length === 0) { toast.error("Tilføj mindst én vare"); return null; }
    setSaving(true);
    const pub = publishNow === undefined ? published : publishNow;
    try {
      const body = { store, name, published: pub, valid_from: validFrom, valid_to: validTo, items: items.map(({ cost, ...it }) => ({ ...it, normal_price: num(it.normal_price), offer_price: num(it.offer_price) })) };
      let id = currentId, sl = slug;
      if (currentId) await api.put(`/avis/${currentId}`, body);
      else { const { data } = await api.post(`/avis`, body); id = data.id; sl = data.slug; setCurrentId(id); setName(data.name); setSlug(sl); }
      setPublished(pub);
      await loadList();
      toast.success(pub ? "Avis gemt og offentliggjort" : "Avis gemt");
      return { id, slug: sl };
    } catch { toast.error("Kunne ikke gemme"); return null; }
    finally { setSaving(false); }
  };

  const publicUrl = useMemo(() => (slug ? `${PUBLIC_BASE}/avis/${slug}` : ""), [slug]);
  const copyUrl = () => { if (!publicUrl) return; navigator.clipboard.writeText(publicUrl); toast.success("Link kopieret"); };

  const openAvis = (a) => { setCurrentId(a.id); setStore(a.store || "falster"); setName(a.name); setPublished(a.published); setValidFrom(a.valid_from || ""); setValidTo(a.valid_to || ""); setItems(a.items || []); setSlug(a.slug || ""); toast.success(`Åbnede "${a.name}"`); };
  const newAvis = () => { setCurrentId(null); setName(""); setPublished(false); setValidFrom(""); setValidTo(""); setItems([]); setSlug(""); };
  const delAvis = async (a) => { if (!window.confirm(`Slet "${a.name}"?`)) return; try { await api.delete(`/avis/${a.id}`); if (currentId === a.id) newAvis(); loadList(); toast.success("Slettet"); } catch { toast.error("Kunne ikke slette"); } };

  return (
    <div className="space-y-5" data-testid="tilbudsavis">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2"><Newspaper className="w-5 h-5 text-rose-500" /> Tilbudsavis</h4>
          <p className="text-sm text-slate-500">Vælg varer fra Customers 1st, sæt tilbudspris og del avisen som et offentligt link.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-[#e2e8f0] overflow-hidden" data-testid="avis-store-select">
            <Store className="w-4 h-4 text-slate-400 ml-2.5 mr-1" />
            {STORES.map((s) => (
              <button key={s.v} onClick={() => setStore(s.v)} data-testid={`avis-store-${s.v}`}
                className={`px-3 py-1.5 text-sm transition-colors ${store === s.v ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{s.l}</button>
            ))}
          </div>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Navn (fx Uge 42)" className="w-full sm:w-44" data-testid="avis-name" />
          <Button onClick={() => save()} disabled={saving} className="rounded-lg bg-slate-800 text-white hover:bg-slate-900 gap-1.5" data-testid="avis-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Gem</Button>
          <Button onClick={() => save(true)} disabled={saving} className="rounded-lg bg-rose-600 text-white hover:bg-rose-700 gap-1.5" data-testid="avis-publish-btn"><Globe className="w-4 h-4" />Offentliggør</Button>
          <Button onClick={newAvis} variant="ghost" className="rounded-lg gap-1.5 text-slate-500" data-testid="avis-new-btn"><Plus className="w-4 h-4" />Ny</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-[#e2e8f0] rounded-xl px-3 py-2.5" data-testid="avis-validity">
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Gyldig fra</span>
          <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="h-8 w-40" data-testid="avis-valid-from" />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Gyldig til</span>
          <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className="h-8 w-40" data-testid="avis-valid-to" />
        </div>
        {isoWeek(validFrom) && (
          <span className="inline-flex items-center gap-1.5 bg-rose-600 text-white font-bold text-sm rounded-lg px-3 py-1.5" data-testid="avis-week-badge">
            Uge {isoWeek(validFrom)}
          </span>
        )}
      </div>

      {slug && (
        <div className="flex flex-wrap items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="avis-public-link">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{published ? "Offentlig" : "Kladde"}</span>
          <span className="text-sm text-slate-600 truncate flex-1 min-w-[180px]">{publicUrl}</span>
          <Button onClick={copyUrl} variant="outline" size="sm" className="rounded-lg gap-1.5 h-8" data-testid="avis-copy-url"><Copy className="w-3.5 h-3.5" />Kopiér link</Button>
          <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-lg gap-1.5 h-8" data-testid="avis-preview"><Eye className="w-3.5 h-3.5" />Se avis</Button></a>
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Søg vare eller stregkode i ${store === "falster" ? "Falster" : "Ringsted"}…`} className="pl-9" data-testid="avis-search" />
        {(searching || results.length > 0) && search.trim().length >= 2 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-[#e2e8f0] rounded-xl shadow-lg max-h-72 overflow-auto" data-testid="avis-search-results">
            {searching && <p className="text-sm text-slate-400 px-3 py-2">Søger…</p>}
            {!searching && results.length === 0 && <p className="text-sm text-slate-400 px-3 py-2">Ingen varer fundet</p>}
            {results.map((p) => (
              <button key={p.productno} onClick={() => addItem(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3 border-b last:border-b-0 border-[#f1f5f9]" data-testid={`avis-result-${p.productno}`}>
                <span className="text-sm text-slate-800 truncate">{p.title}<span className="text-slate-400 text-xs ml-2">{p.ean || p.productno}</span></span>
                <span className="text-sm text-slate-500 whitespace-nowrap">{kr(p.price)} kr</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center" data-testid="avis-empty">Søg efter varer ovenfor og tilføj dem til avisen.</p>
      ) : (
        <div className="space-y-2" data-testid="avis-items">
          {items.map((it, i) => (
            <div key={it.productno} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#e2e8f0] bg-white p-2.5" data-testid={`avis-item-${it.productno}`}>
              <div className="w-14 h-14 rounded-lg bg-[#f8fafc] overflow-hidden flex items-center justify-center shrink-0">
                {it.image_path ? <img src={avisImg(it.image_path)} alt={it.title} className="w-full h-full object-cover" /> : <ImageDown className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-medium text-slate-800 leading-tight">{it.title}</p>
                <p className="text-[11px] text-slate-400">{it.ean || it.productno}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400">Normalpris</span>
                  <Input value={it.normal_price} onChange={(e) => upd(i, { normal_price: e.target.value })} inputMode="decimal" className="h-8 w-24 text-right" data-testid={`avis-normal-${it.productno}`} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-rose-500 font-medium">Tilbudspris</span>
                  <Input value={it.offer_price || ""} onChange={(e) => upd(i, { offer_price: e.target.value })} inputMode="decimal" placeholder="0,00" className="h-8 w-24 text-right border-rose-300 focus-visible:ring-rose-400" data-testid={`avis-offer-${it.productno}`} />
                </div>
              </div>
              {(() => { const r = calc(it.offer_price, it.cost); return (
                <div className="flex flex-col min-w-[120px]" data-testid={`avis-avance-${it.productno}`}>
                  <span className="text-[10px] text-slate-400">Kostpris {kr(it.cost || 0)} kr</span>
                  {r ? (
                    <span className={`text-xs font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`avis-avance-val-${it.productno}`}>Avance {kr(r.profit)} kr · DG {r.dg.toFixed(0)}%</span>
                  ) : (<span className="text-xs text-slate-300">Sæt tilbudspris</span>)}
                </div>
              ); })()}
              <div className="flex items-center gap-1">
                <Button onClick={() => fetchImage(i)} disabled={imgBusy === i} variant="outline" size="sm" className="rounded-lg gap-1 h-8" data-testid={`avis-fetch-img-${it.productno}`}>{imgBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageDown className="w-3.5 h-3.5" />}Hent billede</Button>
                <label className="inline-flex items-center justify-center rounded-lg h-8 w-8 border border-[#e2e8f0] text-slate-500 hover:bg-slate-50 cursor-pointer" title="Upload billede fra computer/mobil" data-testid={`avis-upload-${it.productno}`}>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImage(i, e.target.files[0]); e.target.value = ""; }} />
                  <Upload className="w-3.5 h-3.5" />
                </label>
                <Button onClick={() => imageFromUrl(i)} variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-slate-400" title="Indsæt billed-URL" data-testid={`avis-img-url-${it.productno}`}><Link2 className="w-3.5 h-3.5" /></Button>
                <Button onClick={() => removeItem(i)} variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-slate-400 hover:text-rose-600" data-testid={`avis-remove-${it.productno}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#f1f5f9]" data-testid="avis-saved">
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" />Gemte aviser:</span>
          {list.map((a) => (
            <span key={a.id} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium border ${currentId === a.id ? "bg-slate-800 text-white border-slate-800" : "bg-white border-[#e2e8f0] text-slate-600"}`}>
              <button onClick={() => openAvis(a)} data-testid={`avis-open-${a.id}`}>{a.name} · {a.store === "falster" ? "Falster" : "Ringsted"}{a.published ? " ●" : ""}</button>
              <button onClick={() => delAvis(a)} className="opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
